import type { NavigationGuard, RouteRecordRaw } from 'vue-router';
import type { SupervisorRequirement } from '@neon-one/auctions-types';
import { useSupervisorStore } from './store';

// Runs after the shell's requiresSupervisor guard has confirmed a supervisor
// session. The server refuses these routes' API calls anyway; this keeps a
// supervisor without the permission off a page that would only fail. A failed
// access read lands on the lobby too, rather than rendering a page blind.
function requireAccess(requirement: SupervisorRequirement): NavigationGuard {
  return async () => {
    const store = useSupervisorStore();
    try {
      await store.loadAccess();
    } catch {
      return { name: 'supervisor-lobby' };
    }
    return store.can(requirement) ? true : { name: 'supervisor-lobby' };
  };
}

export const routes: RouteRecordRaw[] = [
  {
    path: '/supervisor',
    name: 'supervisor-lobby',
    component: () => import('./pages/SupervisorLobbyPage.vue'),
    meta: { requiresSupervisor: true },
  },
  // Static routes first — `/supervisor/orgs/:salesforceId` below is a dynamic
  // sibling and would swallow any static path declared after it.
  {
    path: '/supervisor/metrics',
    name: 'supervisor-metrics',
    component: () => import('./pages/SupervisorMetricsPage.vue'),
    meta: { requiresSupervisor: true },
  },
  {
    path: '/supervisor/calendar',
    name: 'supervisor-calendar',
    component: () => import('./pages/SupervisorCalendarPage.vue'),
    meta: { requiresSupervisor: true },
  },
  {
    path: '/supervisor/stuck-charges',
    name: 'supervisor-stuck-charges',
    component: () => import('./pages/StuckChargesPage.vue'),
    meta: { requiresSupervisor: true },
    beforeEnter: requireAccess('super_admin'),
  },
  {
    path: '/supervisor/orgs/:salesforceId',
    name: 'supervisor-org-detail',
    component: () => import('./pages/OrgDetailPage.vue'),
    meta: { requiresSupervisor: true },
  },
  {
    path: '/supervisor/orgs/new',
    name: 'supervisor-provision',
    component: () => import('./pages/OrgProvisionPage.vue'),
    meta: { requiresSupervisor: true },
    beforeEnter: requireAccess('provisioning'),
  },
  {
    path: '/supervisor/manage',
    name: 'supervisor-manage',
    component: () => import('./pages/SupervisorManagementPage.vue'),
    meta: { requiresSupervisor: true },
    beforeEnter: requireAccess('super_admin'),
  },
  {
    path: '/supervisor/flags',
    name: 'supervisor-flags',
    component: () => import('./pages/FeatureFlagsPage.vue'),
    meta: { requiresSupervisor: true },
    beforeEnter: requireAccess('super_admin'),
  },
];
